import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { isPublicDemoRoute } from '@/lib/public-demo-routes';
import { PrioritySettings } from '@/components/priority/PrioritySelector';

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
}

const defaultPriorities: PrioritySettings = {
  cash_flow: 'medium',
  payment_acceleration: 'medium',
  reconciliation: 'medium',
  compliance: 'medium',
  reporting: 'medium',
  expenses: 'medium'
};

export function usePriorities() {
  const [priorities, setPriorities] = useState<PrioritySettings>(defaultPriorities);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPriorities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // First try to load from localStorage for immediate access
      const storedPriorities = localStorage.getItem('userPriorities');
      if (storedPriorities) {
        try {
          const parsed = JSON.parse(storedPriorities);
          setPriorities(parsed);
        } catch (parseError) {
          console.error('Error parsing stored priorities:', parseError);
          localStorage.removeItem('userPriorities');
        }
      }

      // Then sync with API — skip on public demo routes (Toyota / Nexteer /
      // Katcon / TREMEC / GDLS). Those pages have no signed-in user and the
      // API may be unreachable on dev machines (port 5000 collision with
      // macOS Control Center / AirPlay Receiver), which triggers CORS errors
      // that fail PRD-1 AC1 ("all 7 tabs render without console errors").
      // Localstorage default is the correct state for an anonymous visitor.
      // Gap-close #3 (P0) — PRD-1.
      if (!isPublicDemoRoute()) {
        try {
          const result = await apiClient.get<ApiResponse<any>>('/companies/current-user');
          if (!result.error && result.data?.priorities) {
            const dbPriorities = result.data.priorities as PrioritySettings;
            setPriorities(dbPriorities);
            localStorage.setItem('userPriorities', JSON.stringify(dbPriorities));
          }
        } catch {
          console.log('API access failed, using localStorage priorities');
        }
      }
    } catch (err) {
      console.error('Error loading priorities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePriorities = useCallback(async (newPriorities: PrioritySettings) => {
    try {
      setError(null);
      setPriorities(newPriorities);
      localStorage.setItem('userPriorities', JSON.stringify(newPriorities));

      try {
        await apiClient.put<ApiResponse<any>>('/companies/current-user/priorities', {
          priorities: newPriorities,
        });
      } catch {
        console.log('API update failed, priorities saved locally only');
      }
    } catch (err) {
      console.error('Error updating priorities:', err);
      setError('Failed to update priorities, but changes are saved locally');
    }
  }, []);

  const updatePriority = useCallback(async (
    area: keyof PrioritySettings,
    priority: 'high' | 'medium' | 'low'
  ) => {
    const newPriorities = { ...priorities, [area]: priority };
    await updatePriorities(newPriorities);
  }, [priorities, updatePriorities]);

  const resetPriorities = useCallback(async () => {
    await updatePriorities(defaultPriorities);
  }, [updatePriorities]);

  useEffect(() => {
    loadPriorities();
  }, [loadPriorities]);

  return {
    priorities,
    loading,
    error,
    updatePriorities,
    updatePriority,
    resetPriorities,
    loadPriorities
  };
}
